import React, { useRef, useState, useEffect, useCallback } from 'react';
import Oscar from './Oscar';

/**
 * Recorder — camera setup, pre-record quality check, 45s record with live coaching,
 * and preview. Coaching SUGGESTS, never blocks. Records locally; hands the final Blob
 * up via onSubmit (parent uploads). iOS: prefers mp4 when webm is unsupported.
 *
 * Quality signals (all lightweight, ~3Hz, off the hot path, fully skippable):
 *  • lighting → average luminance of a downscaled canvas frame
 *  • audio    → RMS level from a Web Audio AnalyserNode
 *  • framing  → native FaceDetector where available (absent = skipped, never blocks)
 */
function pickMime() {
  const opts = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
  for (const t of opts) { try { if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t; } catch (e) { /* */ } }
  return '';
}
const supported = () => !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);

export default function Recorder({ celebrant = 'her', maxSeconds = 45, maxTakes = 3, prompts = [], voiceChip = null, onRecordingStart, onSubmit, onUnsupported }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const faceRef = useRef(null);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const countRef = useRef([]);
  const streaksRef = useRef(null);   // per-signal debounce so coaching never flickers
  const mountedRef = useRef(false);
  const setupSeqRef = useRef(0);     // invalidates a late getUserMedia result (notably React StrictMode)

  const [phase, setPhase] = useState('init');     // init | denied | unsupported | check | ready | recording | preview
  const [mime, setMime] = useState('');
  const [coach, setCoach] = useState({ light: null, audio: null, face: null });   // null=ok, string=tip
  const [left, setLeft] = useState(maxSeconds);
  const [takes, setTakes] = useState(0);
  const [count, setCount] = useState(null);    // 3 · 2 · 1 · Action! pre-roll (null when idle)
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const promptIdx = Math.min(takes, Math.max(0, prompts.length - 1));

  // Release every camera/microphone resource explicitly. Relying only on React
  // unmount can leak the first async getUserMedia stream under development
  // StrictMode, where effects intentionally mount, clean up, and mount again.
  const stopAll = useCallback(() => {
    setupSeqRef.current += 1;
    try { cancelAnimationFrame(rafRef.current); } catch (e) { /* */ }
    rafRef.current = null;
    try { clearInterval(tickRef.current); } catch (e) { /* */ }
    tickRef.current = null;
    try { countRef.current.forEach(clearTimeout); countRef.current = []; } catch (e) { /* */ }
    try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch (e) { /* */ }
    recRef.current = null;
    const stream = streamRef.current;
    streamRef.current = null;
    try { (stream ? stream.getTracks() : []).forEach((track) => track.stop()); } catch (e) { /* */ }
    try {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    } catch (e) { /* */ }
    analyserRef.current = null;
    try { if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') audioCtxRef.current.close(); } catch (e) { /* */ }
    audioCtxRef.current = null;
  }, []);

  // ── camera + mic setup ──
  const setup = useCallback(async () => {
    if (!supported()) { setPhase('unsupported'); onUnsupported && onUnsupported(); return; }
    const setupSeq = setupSeqRef.current + 1;
    setupSeqRef.current = setupSeq;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      // A resolved stream may belong to an effect that React has already cleaned
      // up. Stop it immediately instead of leaving the device indicator active.
      if (!mountedRef.current || setupSeq !== setupSeqRef.current) {
        try { stream.getTracks().forEach((track) => track.stop()); } catch (e) { /* */ }
        return;
      }
      try { (streamRef.current ? streamRef.current.getTracks() : []).forEach((track) => track.stop()); } catch (e) { /* */ }
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.muted = true; }
      setMime(pickMime());
      // audio analyser
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx(); audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const an = ctx.createAnalyser(); an.fftSize = 512; src.connect(an); analyserRef.current = an;
      } catch (e) { /* audio metering optional */ }
      try { if ('FaceDetector' in window) faceRef.current = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 }); } catch (e) { /* */ }
      setPhase('check');
    } catch (e) {
      if (!mountedRef.current || setupSeq !== setupSeqRef.current) return;
      setPhase(e && e.name === 'NotAllowedError' ? 'denied' : 'unsupported');
    }
  }, [onUnsupported]);

  useEffect(() => {
    mountedRef.current = true;
    setup();
    return () => {
      mountedRef.current = false;
      stopAll();
    };
  }, [setup, stopAll]);

  // ── live quality loop (check + recording) ──
  useEffect(() => {
    if (phase !== 'check' && phase !== 'recording') return undefined;
    const cv = document.createElement('canvas'); cv.width = 64; cv.height = 48; const cx = cv.getContext('2d');
    let last = 0, running = true, faceBusy = false;
    // Debounce each signal with hysteresis so a tip only appears after it persists, and
    // only clears after things have been good a while — calm, never blinking.
    const ON_NEED = 6, OFF_NEED = 8;   // ~2s to show, ~2.5s to clear (loop ~320ms)
    streaksRef.current = { light: { bad: 0, good: 0, on: false, shown: null },
                           audio: { bad: 0, good: 0, on: false, shown: null },
                           face:  { bad: 0, good: 0, on: false, shown: null } };
    const bump = (key, bad, tip) => {
      const s = streaksRef.current[key];
      if (bad) { s.bad++; s.good = 0; } else { s.good++; s.bad = 0; }
      if (!s.on) {
        if (s.bad >= ON_NEED && tip) { s.on = true; s.shown = tip; setCoach((c) => ({ ...c, [key]: tip })); }
      } else if (s.good >= OFF_NEED) {
        s.on = false; s.shown = null; setCoach((c) => ({ ...c, [key]: null }));
      } else if (bad && tip && tip !== s.shown) {   // text changed while still on (e.g. dark↔bright)
        s.shown = tip; setCoach((c) => ({ ...c, [key]: tip }));
      }
    };
    const loop = async (ts) => {
      if (!running) return;
      if (ts - last > 320) {
        last = ts;
        const v = videoRef.current;
        if (v && v.videoWidth) {
          // lighting
          try {
            cx.drawImage(v, 0, 0, 64, 48); const d = cx.getImageData(0, 0, 64, 48).data; let sum = 0;
            for (let i = 0; i < d.length; i += 4) sum += (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114);
            const lum = sum / (d.length / 4);
            const lightTip = lum < 50 ? 'A brighter spot would help' : (lum > 240 ? 'A softer light would help' : null);
            bump('light', lightTip !== null, lightTip);
          } catch (e) { /* */ }
          // face / framing
          if (faceRef.current && !faceBusy) {
            faceBusy = true;
            faceRef.current.detect(v).then((faces) => {
              bump('face', !faces || !faces.length, 'Center your face in the frame');
            }).catch(() => {}).finally(() => { faceBusy = false; });
          }
        }
        // audio
        const an = analyserRef.current;
        if (an) {
          const buf = new Uint8Array(an.fftSize); an.getByteTimeDomainData(buf);
          let s = 0; for (let i = 0; i < buf.length; i++) { const x = (buf[i] - 128) / 128; s += x * x; }
          const rms = Math.sqrt(s / buf.length);
          bump('audio', rms < 0.01, 'Speak up a little so ' + celebrant + ' can hear you');
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { running = false; cancelAnimationFrame(rafRef.current); };
  }, [phase, celebrant]);

  // ── 3·2·1·Action! pre-roll, then roll the camera ──
  const startCountdown = () => {
    if (!streamRef.current || count !== null) return;
    if (onRecordingStart) onRecordingStart();
    countRef.current.forEach(clearTimeout); countRef.current = [];
    countRef.current.push(setTimeout(() => setCount(3), 0));
    countRef.current.push(setTimeout(() => setCount(2), 750));
    countRef.current.push(setTimeout(() => setCount(1), 1500));
    countRef.current.push(setTimeout(() => { setCount('Action!'); startRecording(); }, 2250));
    countRef.current.push(setTimeout(() => setCount(null), 3050));   // "Action!" lingers ~0.8s over the live take
  };

  // ── recording ──
  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    let rec;
    try { rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined); }
    catch (e) { try { rec = new MediaRecorder(streamRef.current); } catch (e2) { setPhase('unsupported'); return; } }
    recRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const type = (mime && mime.split(';')[0]) || 'video/webm';
      const blob = new Blob(chunksRef.current, { type });
      const dur = Math.min(maxSeconds, Math.round((Date.now() - startedAtRef.current) / 1000));
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewBlob({ blob, dur, type });
      setPreviewUrl(URL.createObjectURL(blob));
      setTakes((t) => t + 1);
      setPhase('preview');
    };
    startedAtRef.current = Date.now();
    rec.start(1000);   // 1s timeslices → resumable-friendly chunks
    setLeft(maxSeconds);
    setPhase('recording');
    tickRef.current = setInterval(() => {
      const elapsed = Math.round((Date.now() - startedAtRef.current) / 1000);
      const rem = Math.max(0, maxSeconds - elapsed);
      setLeft(rem);
      if (rem <= 0) stopRecording();
    }, 250);
  };
  const stopRecording = () => {
    try { clearInterval(tickRef.current); } catch (e) { /* */ }
    try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch (e) { /* */ }
  };

  const useThisTake = () => {
    if (!previewBlob || !onSubmit) return;
    // Privacy first: turn the camera and microphone off before upload or any
    // thank-you/finished state becomes visible.
    stopAll();
    onSubmit(previewBlob.blob, previewBlob.dur, previewBlob.type, takes);
  };
  const recordAgain = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setPreviewBlob(null);
    if (videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current;
    setPhase('ready');
  };
  const takesLeft = Math.max(0, maxTakes - takes);
  // Surface at most ONE gentle nudge at a time (visual first), and only when it applies.
  const tips = [coach.light, coach.face, coach.audio].filter(Boolean);

  if (phase === 'unsupported') {
    return <div className="vgb-rec-msg"><Oscar pose="heart" size={120} /><h2 className="vgb-h">Let’s use a different browser</h2>
      <p className="vgb-sub">This phone’s browser can’t record video. Please open this link in a recent Safari or Chrome and try again.</p></div>;
  }
  if (phase === 'denied') {
    return <div className="vgb-rec-msg"><Oscar pose="heart" size={120} /><h2 className="vgb-h">Camera access needed</h2>
      <p className="vgb-sub">To record your message, please allow camera &amp; microphone, then tap below.</p>
      <button className="vgb-btn" onClick={setup} style={{ marginTop: 22 }}>Try again</button></div>;
  }

  return (
    <div className={`vgb-rec vgb2-recorder vgb2-recorder-${phase}`}>
      <header className="vgb2-recorder-heading">
        <span className="vgb2-recorder-step">{phase === 'preview' ? 'Review your message' : 'Carol’s video keepsake'}</span>
        <h1>{phase === 'preview' ? 'Ready to send?' : `A message for ${celebrant}`}</h1>
        <p>{phase === 'preview'
          ? 'Watch it once, then send it to Carol or record another take.'
          : 'Look into the camera and speak from the heart.'}</p>
      </header>

      <div className={`vgb2-media-frame is-${phase}`}>
      {/* camera stage — the hero. Controls and coaching float ON the view so it fills the
          screen. Kept mounted during preview (stream alive) but hidden. */}
      <div className="vgb-cam" style={phase === 'preview' ? { display: 'none' } : undefined}>
        <video ref={videoRef} autoPlay playsInline muted className="vgb-video" />
        {phase === 'init' && <div className="vgb-cam-load"><Oscar size={96} /><p className="vgb-sub">Waking up the camera…</p></div>}

        {phase === 'recording' && (
          <div className="vgb-countdown" aria-live="polite">
            <span className={`vgb-rdot ${left <= 5 ? 'urgent' : ''}`} /> {left}s
          </div>
        )}

        {/* 3 · 2 · 1 · Action! pre-roll — big, playful, over the live view */}
        {count !== null && (
          <div className={`vgb-count ${count === 'Action!' ? 'action' : ''}`} aria-live="assertive">
            <span key={String(count)}>{count === 'Action!' ? '🎬 Action!' : count}</span>
          </div>
        )}

        {/* record / stop — floats on the camera (hidden while counting down) */}
        {(phase === 'check' || phase === 'ready') && count === null && (
          <div className="vgb-recbtn-wrap">
            <button className="vgb-recbtn" onClick={startCountdown} aria-label="Start recording"><span className="dot" /></button>
            <span className="vgb-cap">{takes > 0 ? `Tap to record · take ${takes + 1}` : 'Tap to record'}</span>
          </div>
        )}
        {phase === 'recording' && (
          <div className="vgb-recbtn-wrap">
            <button className="vgb-recbtn recording" onClick={stopRecording} aria-label="Stop recording"><span className="sq" /></button>
          </div>
        )}
      </div>

        {phase === 'preview' && previewUrl && (
          <div className="vgb-prev vgb2-review-video">
            <video src={previewUrl} controls playsInline preload="metadata" className="vgb-video" aria-label="Review your recorded message" />
            <span className="vgb2-review-badge"><span aria-hidden="true">✓</span> Take {takes} ready</span>
          </div>
        )}
      </div>

      {/* elegant coaching card — below the view, where the prompt used to be. Shows the one
          applicable suggestion, or a calm reassurance + the take/length heads-up. */}
      {(phase === 'check' || phase === 'recording') && (
        <div className="vgb-coachcard">
          <div className="vgb-coachcard-head">
            <span className="vgb-coachcard-eye">✦ Oscar</span>
            {voiceChip}
          </div>
          <p className="vgb-coachcard-text" aria-live="polite">
            {tips[0] || (phase === 'recording'
              ? 'Beautiful — keep going.'
              : 'You look and sound lovely. Tap record when you’re ready.')}
          </p>
          <span className="vgb-coachcard-meta">Up to {maxSeconds}s · {Math.max(1, maxTakes - takes)} of {maxTakes} {maxTakes - takes === 1 ? 'try' : 'tries'} left</span>
        </div>
      )}

      {/* preview */}
      {phase === 'preview' && previewUrl && (
        <div className="vgb2-review-actions">
          <div className="vgb2-review-note"><strong>Your camera is still available</strong><span>It switches off as soon as you send this take.</span></div>
          <div className="vgb2-state-actions">
            <button className="vgb2-primary" onClick={useThisTake}><span>Send to {celebrant}</span><span className="vgb2-primary-arrow" aria-hidden="true">→</span></button>
            {takesLeft > 0
              ? <button className="vgb2-text-action" onClick={recordAgain}>Record again · {takesLeft} {takesLeft === 1 ? 'take' : 'takes'} left</button>
              : <span className="vgb2-no-takes">You’ve used all available takes.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
